import React, { useMemo, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "./ResetPasswordPage.scss";
import { submitPasswordReset } from "@/api/auth";
import { GenericButton } from "@/components/GenericButton/GenericButton";
import { ReactComponent as TieOutLogo } from "@/assets/icons/tie-out.svg";
import { ReactComponent as RecoverPasswordIcon } from "@/assets/icons/recover-password.svg";
import { ReactComponent as LockIcon } from "@/assets/icons/lock.svg";
import { ReactComponent as ShieldIcon } from "@/assets/icons/shield.svg";
import { ReactComponent as EyeIcon } from "@/assets/icons/eye.svg";
import { ReactComponent as EyeOffIcon } from "@/assets/icons/eye-off.svg";

type FormValues = {
  password: string;
  confirmPassword: string;
};

export const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    setErrorMessage(null);

    if (data.password !== data.confirmPassword) {
      setErrorMessage(t("pages.auth.resetPassword.errors.passwordMismatch"));
      return;
    }

    submitPasswordReset({ token, newPassword: data.password })
      .then(() => {
        setIsSuccess(true);
      })
      .catch(() => {
        setErrorMessage(t("pages.auth.resetPassword.errors.resetFailed"));
      });
  };

  const hasToken = Boolean(token);

  return (
    <div className="reset-password-page">
      <TieOutLogo className="reset-password-logo" />

      <div className="reset-password-card">
        <div className="reset-password-icon-wrap">
          <RecoverPasswordIcon className="reset-password-icon" aria-hidden />
        </div>

        <h1 className="reset-password-title">
          {isSuccess
            ? t("pages.auth.resetPassword.successTitle")
            : t("pages.auth.resetPassword.title")}
        </h1>

        {isSuccess ? (
          <div className="reset-password-success">
            <p className="reset-password-success-message">
              {t("pages.auth.resetPassword.successMessage")}
            </p>
            <GenericButton
              label={t("pages.auth.resetPassword.actions.goToLogin")}
              onClick={() => navigate("/login")}
            />
          </div>
        ) : hasToken ? (
          <>
            <p className="reset-password-subtitle">
              {t("pages.auth.resetPassword.subtitle")}
            </p>

            <form
              className="reset-password-form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <div className="reset-password-field">
                <label
                  className="reset-password-label"
                  htmlFor="reset-password-new"
                >
                  {t("pages.auth.resetPassword.fields.newPassword")}
                </label>
                <div className="reset-password-input-wrap">
                  <LockIcon className="reset-password-input-icon" aria-hidden />
                  <input
                    id="reset-password-new"
                    className="reset-password-input reset-password-password-input"
                    type={isPasswordVisible ? "text" : "password"}
                    autoComplete="new-password"
                    {...register("password", { required: true, minLength: 12 })}
                  />
                  <button
                    type="button"
                    className="reset-password-password-toggle"
                    onClick={() => setIsPasswordVisible((v) => !v)}
                    aria-label={t(
                      "pages.auth.login.actions.togglePasswordVisibility",
                    )}
                  >
                    {isPasswordVisible ? (
                      <EyeOffIcon
                        className="reset-password-password-toggle-icon"
                        aria-hidden
                      />
                    ) : (
                      <EyeIcon
                        className="reset-password-password-toggle-icon"
                        aria-hidden
                      />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <span className="reset-password-error">
                    {errors.password.type === "minLength"
                      ? t("pages.auth.resetPassword.errors.passwordMinLength")
                      : t("pages.auth.resetPassword.errors.passwordRequired")}
                  </span>
                )}
              </div>

              <div className="reset-password-field">
                <label
                  className="reset-password-label"
                  htmlFor="reset-password-confirm"
                >
                  {t("pages.auth.resetPassword.fields.confirmPassword")}
                </label>
                <div className="reset-password-input-wrap">
                  <ShieldIcon
                    className="reset-password-input-icon"
                    aria-hidden
                  />
                  <input
                    id="reset-password-confirm"
                    className="reset-password-input reset-password-password-input"
                    type={isConfirmPasswordVisible ? "text" : "password"}
                    autoComplete="new-password"
                    {...register("confirmPassword", { required: true })}
                  />
                  <button
                    type="button"
                    className="reset-password-password-toggle"
                    onClick={() => setIsConfirmPasswordVisible((v) => !v)}
                    aria-label={t(
                      "pages.auth.login.actions.togglePasswordVisibility",
                    )}
                  >
                    {isConfirmPasswordVisible ? (
                      <EyeOffIcon
                        className="reset-password-password-toggle-icon"
                        aria-hidden
                      />
                    ) : (
                      <EyeIcon
                        className="reset-password-password-toggle-icon"
                        aria-hidden
                      />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span className="reset-password-error">
                    {t(
                      "pages.auth.resetPassword.errors.confirmPasswordRequired",
                    )}
                  </span>
                )}
              </div>

              {errorMessage && (
                <p className="reset-password-error">{errorMessage}</p>
              )}

              <div className="reset-password-submit">
                <GenericButton
                  label={t("pages.auth.resetPassword.actions.updatePassword")}
                  theme="primary"
                />
              </div>
            </form>
          </>
        ) : (
          <p className="reset-password-token-error">
            {t("pages.auth.resetPassword.errors.missingToken")}
          </p>
        )}
      </div>

      <Link className="reset-password-back" to="/login">
        {t("pages.auth.resetPassword.backToLogin")}
      </Link>
    </div>
  );
};
